import { FormEvent, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import type { ModelInfo, OrgTeamsConfigSummary } from 'chat-shared'
import { api, ApiError } from '../api/client.js'
import { AdminCard } from './AdminUi.js'

/** Wert der Dropdown-Option „Server-Default" (kein Org-Override). */
const MODEL_DEFAULT = ''

// Ergebnis des Admin-Consent-Flows (Query-Parameter des Callback-Redirects,
// siehe apps/chat-api/src/http/teamsConsentCallback.ts).
const CONSENT_MESSAGES: Record<
  string,
  { severity: 'success' | 'error'; text: string }
> = {
  success: {
    severity: 'success',
    text: 'Microsoft-365-Tenant verbunden — die Teams-Anbindung ist aktiv.',
  },
  declined: {
    severity: 'error',
    text: 'Die Zustimmung wurde nicht erteilt. Bitte den Vorgang erneut starten.',
  },
  tenant_already_connected: {
    severity: 'error',
    text: 'Dieser Microsoft-Tenant ist bereits mit einer anderen Organisation verbunden.',
  },
  verify_failed: {
    severity: 'error',
    text: 'Die Zustimmung konnte nicht verifiziert werden. Bitte erneut versuchen.',
  },
  invalid_state: {
    severity: 'error',
    text: 'Der Verbindungs-Link ist abgelaufen. Bitte den Vorgang erneut starten.',
  },
  teams_disabled: {
    severity: 'error',
    text: 'Die Teams-Anbindung ist auf diesem Server nicht konfiguriert.',
  },
  error: {
    severity: 'error',
    text: 'Verbinden fehlgeschlagen. Bitte erneut versuchen.',
  },
}

type OrgTeamsSectionProps = {
  orgId: string
}

// Microsoft-Teams-Anbindung der Organisation: alle Orgs teilen sich den
// globalen HappyOps-Bot; verbunden wird der Microsoft-365-Tenant der Org über
// den Entra-Admin-Consent-Flow. Beim Verbinden provisioniert der Server
// Service-Konto, Ordner „Teams-Bot" und den Bot-API-Schlüssel automatisch
// (siehe apps/chat-api/src/teams/provisioning.ts).
export const OrgTeamsSection = ({ orgId }: OrgTeamsSectionProps) => {
  const [config, setConfig] = useState<OrgTeamsConfigSummary | undefined>(
    undefined,
  )
  const [enabled, setEnabled] = useState(false)
  const [model, setModel] = useState<string>(MODEL_DEFAULT)
  const [models, setModels] = useState<Array<ModelInfo>>([])
  const [error, setError] = useState<string | undefined>(undefined)
  const [notice, setNotice] = useState<
    { severity: 'success' | 'error'; text: string } | undefined
  >(undefined)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [searchParams, setSearchParams] = useSearchParams()

  const applyConfig = (summary: OrgTeamsConfigSummary | undefined) => {
    setConfig(summary)
    setEnabled(summary?.enabled ?? false)
    setModel(summary?.model ?? MODEL_DEFAULT)
  }

  useEffect(() => {
    api
      .getOrgTeamsConfig(orgId)
      .then(applyConfig)
      .catch(() => applyConfig(undefined))
    api
      .models()
      .then(setModels)
      .catch(() => setModels([]))
    setError(undefined)
    setNotice(undefined)
  }, [orgId])

  // Ergebnis des Consent-Redirects anzeigen und den Parameter aus der URL
  // entfernen (sonst erscheint die Meldung bei jedem Reload erneut).
  useEffect(() => {
    const consent = searchParams.get('consent')
    if (consent === null) return
    setNotice(CONSENT_MESSAGES[consent] ?? CONSENT_MESSAGES.error)
    const next = new URLSearchParams(searchParams)
    next.delete('consent')
    setSearchParams(next, { replace: true })
  }, [searchParams, setSearchParams])

  const handleConnect = async () => {
    setError(undefined)
    try {
      const { url } = await api.startOrgTeamsConsent(orgId)
      window.location.assign(url)
    } catch (err) {
      if (err instanceof ApiError && err.status === 503) {
        setError(
          'Die Teams-Anbindung ist auf diesem Server nicht konfiguriert.',
        )
      } else {
        setError('Verbinden fehlgeschlagen')
      }
    }
  }

  const handleCopyConsentLink = async () => {
    setError(undefined)
    try {
      const { url } = await api.startOrgTeamsConsent(orgId)
      await navigator.clipboard.writeText(url)
      setNotice({
        severity: 'success',
        text: 'Link kopiert — er kann an den Microsoft-365-Administrator der Organisation weitergegeben werden und ist 30 Minuten gültig.',
      })
    } catch {
      setError('Link konnte nicht erstellt werden')
    }
  }

  const handleSave = async (event: FormEvent) => {
    event.preventDefault()
    setError(undefined)
    setNotice(undefined)
    try {
      const summary = await api.saveOrgTeamsConfig(orgId, {
        enabled,
        // „Server-Default" ⇒ kein Override speichern.
        model: model.length > 0 ? model : undefined,
      })
      applyConfig(summary)
      setNotice({ severity: 'success', text: 'Gespeichert.' })
    } catch (err) {
      if (err instanceof ApiError && err.message === 'consent_required') {
        setError('Bitte zuerst den Microsoft-365-Tenant verbinden.')
      } else {
        setError('Speichern fehlgeschlagen')
      }
    }
  }

  const handleDelete = async () => {
    setError(undefined)
    try {
      await api.deleteOrgTeamsConfig(orgId)
      applyConfig(undefined)
      setConfirmDelete(false)
    } catch {
      setError('Löschen fehlgeschlagen')
      setConfirmDelete(false)
    }
  }

  return (
    <AdminCard
      title="Microsoft Teams"
      description="Verbindet den HappyOps-Teams-Bot („@HappyOps“) mit dem Microsoft-365-Tenant dieser Organisation. Ein Microsoft-365-Administrator stimmt der HappyOps-App einmalig zu (Admin-Consent); danach muss die Teams-App nur noch im Teams Admin Center hochgeladen werden. Chats des Bots erscheinen im automatisch angelegten Ordner „Teams-Bot“."
      padded
    >
      {error && (
        <Alert
          severity="error"
          onClose={() => setError(undefined)}
          sx={{ mb: 2 }}
        >
          {error}
        </Alert>
      )}
      {notice && (
        <Alert
          severity={notice.severity}
          onClose={() => setNotice(undefined)}
          sx={{ mb: 2 }}
        >
          {notice.text}
        </Alert>
      )}

      {!config && (
        <Box display="flex" flexDirection="column" gap={2} maxWidth={480}>
          <Typography variant="body2" color="text.secondary">
            Noch kein Microsoft-365-Tenant verbunden. Zum Verbinden ist die
            Zustimmung eines Microsoft-365-Administrators (Global Admin) der
            Organisation nötig — entweder direkt hier oder über einen
            weitergegebenen Link.
          </Typography>
          <Box display="flex" gap={1}>
            <Button variant="contained" onClick={() => void handleConnect()}>
              Mit Microsoft verbinden
            </Button>
            <Button onClick={() => void handleCopyConsentLink()}>
              Link für IT-Admin kopieren
            </Button>
          </Box>
        </Box>
      )}

      {config && (
        <Box
          component="form"
          onSubmit={handleSave}
          display="flex"
          flexDirection="column"
          gap={2}
          maxWidth={480}
        >
          <TextField
            size="small"
            label="Verbundener Entra-Tenant"
            value={config.entraTenantId}
            slotProps={{ input: { readOnly: true } }}
            helperText="Per Admin-Consent verifiziert. Zum Wechseln neu verbinden."
          />
          <TextField
            size="small"
            select
            label="KI-Modell für Bot-Chats"
            value={model}
            onChange={(e) => setModel(e.target.value)}
            helperText="Gilt für neue Bot-Chats; pro Teams-Thread per „@HappyOps set model“ überschreibbar."
          >
            <MenuItem value={MODEL_DEFAULT}>Server-Default</MenuItem>
            {models.map((entry) => (
              <MenuItem key={entry.id} value={entry.id}>
                {entry.label}
              </MenuItem>
            ))}
            {model !== MODEL_DEFAULT &&
              !models.some((entry) => entry.id === model) && (
                <MenuItem value={model}>{model} (deaktiviert)</MenuItem>
              )}
          </TextField>
          <FormControlLabel
            control={
              <Switch
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
              />
            }
            label="Teams-Anbindung aktiv"
          />
          <Box display="flex" gap={1} flexWrap="wrap">
            <Button type="submit" variant="contained">
              Speichern
            </Button>
            <Button onClick={() => void handleConnect()}>Neu verbinden</Button>
            <Button color="error" onClick={() => setConfirmDelete(true)}>
              Anbindung löschen
            </Button>
          </Box>
        </Box>
      )}

      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <DialogTitle>Teams-Anbindung löschen?</DialogTitle>
        <DialogContent>
          Die Tenant-Verbindung wird entfernt und der API-Schlüssel des Bots
          widerrufen. Der Ordner „Teams-Bot“ und die bisherigen Chats bleiben
          erhalten.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)}>Abbrechen</Button>
          <Button color="error" onClick={() => void handleDelete()}>
            Löschen
          </Button>
        </DialogActions>
      </Dialog>
    </AdminCard>
  )
}
